import * as React from "react";
import {
  Area,
  AreaChart as RcAreaChart,
  Bar,
  BarChart as RcBarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart as RcPieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { cn } from "../lib/cn.js";

/**
 * Chart primitives — thin wrappers around recharts that lock in the
 * EduViaOS visual language (palette-aware colours, muted axis ticks,
 * card-friendly tooltips). Pulled from refs:
 *
 *   - AreaChart: ref eduvia-refs-images/Screenshot 003612.jpg
 *     "Sale Activity" — smooth two-tone area with overlapping fills
 *   - BarChart:  refs 003652 (Revenue) + 003711 (Budgets)
 *     Vertical bars, optional second series, light grid lines
 *   - DonutChart: refs 003652 (Total Visitor) + 003825 (Reservations)
 *     Concentric donut with center label + segmented legend
 *
 * Colors flow through the design-tokens stack (`var(--color-primary)`,
 * `var(--color-chart-N)`) so picking a custom theme color recolours
 * the charts automatically.
 *
 * Animation policy: recharts' default 1500ms ease-out enter is too
 * slow for a dashboard that re-renders often. We dial to 300ms which
 * is right at the upper edge of Emil's "UI under 300ms" rule and
 * gives a satisfying "values landing" feel on first paint without
 * dragging on subsequent re-renders.
 */

const CHART_ANIM_MS = 300;

const TICK_STYLE = {
  fill: "var(--color-muted-foreground)",
  fontSize: 11,
  fontFamily: "inherit",
} as const;

/**
 * Styled tooltip body. Recharts gives us a bag of `payload` objects;
 * we render a tight card with one row per series. Same surface treatment
 * as Card so it reads as a "lifted" element on hover.
 */
function ChartTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: Array<{ name: string; value: number; color: string; payload: Record<string, unknown> }>;
  label?: string | number;
}) {
  if (!active || !payload?.length) return null;
  return (
    <div className="border-border bg-card/95 rounded-lg border px-3 py-2 text-xs shadow-md backdrop-blur-sm">
      {label != null ? <p className="text-foreground mb-1 font-medium">{label}</p> : null}
      <div className="grid gap-0.5">
        {payload.map((p, i) => (
          <div key={i} className="flex items-center gap-2">
            <span
              aria-hidden="true"
              className="size-2 shrink-0 rounded-full"
              style={{ background: p.color }}
            />
            <span className="text-muted-foreground">{p.name}</span>
            <span className="text-foreground ml-auto font-mono font-medium tabular-nums">
              {typeof p.value === "number" ? p.value.toLocaleString() : p.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* -----------------------------------------------------------------------
 * AreaChart (ref 003612 — Sale Activity)
 * --------------------------------------------------------------------- */

export interface AreaChartSeries {
  /** Key in the data row this series reads from. */
  dataKey: string;
  /** Display label in tooltip. */
  name: string;
  /** Fill + stroke colour. Accepts CSS variables ("var(--color-primary)"). */
  color: string;
}

export interface AreaChartProps<T extends Record<string, unknown>> {
  data: T[];
  /** X-axis key. Usually a date or label string. */
  xKey: keyof T & string;
  /** One or more areas drawn back-to-front in array order. */
  series: AreaChartSeries[];
  /** Override fixed height (px) — defaults responsive. */
  height?: number;
  /** Hide the X-axis ticks (small embedded sparklines). */
  hideXAxis?: boolean;
  /** Hide the Y-axis ticks. */
  hideYAxis?: boolean;
  /** Set false for count series so the Y axis never shows 0.25 learners. */
  allowDecimals?: boolean;
  className?: string;
}

/**
 * Smooth area chart with translucent gradient fills. Two overlapping
 * series read like the reference's "Sale Activity" tile.
 */
export function AreaChart<T extends Record<string, unknown>>({
  data,
  xKey,
  series,
  height,
  hideXAxis,
  hideYAxis,
  allowDecimals = true,
  className,
}: AreaChartProps<T>) {
  // Stable gradient IDs per chart instance so multiple charts on the
  // same page don't collide in the SVG <defs> namespace.
  const reactId = React.useId().replace(/:/g, "");

  return (
    <div className={cn("h-full w-full", className)} style={height ? { height } : undefined}>
      <ResponsiveContainer width="100%" height={height ?? "100%"}>
        <RcAreaChart
          data={data}
          margin={{ top: 8, right: 8, bottom: hideXAxis ? 0 : 8, left: hideYAxis ? 0 : 8 }}
        >
          <defs>
            {series.map((s, i) => {
              const id = `eos-area-${reactId}-${i}`;
              return (
                <linearGradient key={id} id={id} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={s.color} stopOpacity={0.45} />
                  <stop offset="100%" stopColor={s.color} stopOpacity={0.02} />
                </linearGradient>
              );
            })}
          </defs>
          <CartesianGrid stroke="var(--color-border)" strokeDasharray="3 3" vertical={false} />
          {hideXAxis ? null : (
            <XAxis
              dataKey={xKey}
              tickLine={false}
              axisLine={false}
              tick={TICK_STYLE}
              padding={{ left: 8, right: 8 }}
            />
          )}
          {hideYAxis ? null : (
            <YAxis
              tickLine={false}
              axisLine={false}
              tick={TICK_STYLE}
              width={32}
              allowDecimals={allowDecimals}
            />
          )}
          <Tooltip content={<ChartTooltip />} cursor={{ stroke: "var(--color-border)" }} />
          {series.map((s, i) => {
            const id = `eos-area-${reactId}-${i}`;
            return (
              <Area
                key={s.dataKey}
                type="monotone"
                dataKey={s.dataKey}
                name={s.name}
                stroke={s.color}
                strokeWidth={2}
                fill={`url(#${id})`}
                animationDuration={CHART_ANIM_MS}
              />
            );
          })}
        </RcAreaChart>
      </ResponsiveContainer>
    </div>
  );
}

/* -----------------------------------------------------------------------
 * BarChart (ref 003652 — Revenue, ref 003711 — Budgets)
 * --------------------------------------------------------------------- */

export interface BarChartSeries {
  dataKey: string;
  name: string;
  color: string;
}

export interface BarChartProps<T extends Record<string, unknown>> {
  data: T[];
  xKey: keyof T & string;
  series: BarChartSeries[];
  height?: number;
  /** Bar corner radius. Defaults to 4 — set 0 for a brutalist look. */
  radius?: number;
  /** Set false for count series so the Y axis never shows 0.25 bookings. */
  allowDecimals?: boolean;
  className?: string;
}

export function BarChart<T extends Record<string, unknown>>({
  data,
  xKey,
  series,
  height,
  radius = 4,
  allowDecimals = true,
  className,
}: BarChartProps<T>) {
  return (
    <div className={cn("h-full w-full", className)} style={height ? { height } : undefined}>
      <ResponsiveContainer width="100%" height={height ?? "100%"}>
        <RcBarChart data={data} margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
          <CartesianGrid stroke="var(--color-border)" strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey={xKey} tickLine={false} axisLine={false} tick={TICK_STYLE} />
          <YAxis
            tickLine={false}
            axisLine={false}
            tick={TICK_STYLE}
            width={32}
            allowDecimals={allowDecimals}
          />
          <Tooltip
            content={<ChartTooltip />}
            cursor={{ fill: "var(--color-muted)", opacity: 0.4 }}
          />
          {series.map((s) => (
            <Bar
              key={s.dataKey}
              dataKey={s.dataKey}
              name={s.name}
              fill={s.color}
              radius={[radius, radius, 0, 0]}
              animationDuration={CHART_ANIM_MS}
              maxBarSize={32}
            />
          ))}
        </RcBarChart>
      </ResponsiveContainer>
    </div>
  );
}

/* -----------------------------------------------------------------------
 * DonutChart (ref 003652 — Total Visitor, ref 003825 — Reservations)
 * --------------------------------------------------------------------- */

export interface DonutSegment {
  /** Display label. Used in legend + tooltip. */
  name: string;
  /** Numeric value, summed for the centre total. */
  value: number;
  /** Fill colour. */
  color: string;
}

export interface DonutChartProps {
  segments: DonutSegment[];
  /** Big number rendered in the centre. Defaults to the sum of all segments. */
  centerValue?: React.ReactNode;
  /** Smaller label under the centre value. */
  centerLabel?: string;
  /** Outer radius in viewBox units. Defaults responsive. */
  height?: number;
  /** Inner-radius ratio (0-1). Default 0.62 reads as a thick donut. */
  thickness?: number;
  className?: string;
}

export function DonutChart({
  segments,
  centerValue,
  centerLabel,
  height = 220,
  thickness = 0.62,
  className,
}: DonutChartProps) {
  const total = segments.reduce((acc, s) => acc + s.value, 0);
  // Inner / outer radii are computed by recharts based on the chart
  // dimensions; we express the inner via percentage strings.
  const innerRadius = `${Math.round(thickness * 100)}%`;

  return (
    <div className={cn("relative h-full w-full", className)} style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <RcPieChart>
          <Pie
            data={segments}
            dataKey="value"
            nameKey="name"
            innerRadius={innerRadius}
            outerRadius="92%"
            paddingAngle={2}
            stroke="var(--color-card)"
            strokeWidth={2}
            startAngle={90}
            endAngle={-270}
            animationDuration={CHART_ANIM_MS}
          >
            {segments.map((s, i) => (
              <Cell key={`${s.name}-${i}`} fill={s.color} />
            ))}
          </Pie>
          <Tooltip content={<ChartTooltip />} />
        </RcPieChart>
      </ResponsiveContainer>

      {/* Center label — absolute-positioned because recharts doesn't
          ship a first-class centre-label API. The svg has no pointer
          events from the parent, so the tooltip still works on hover. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 grid place-items-center text-center"
      >
        <div>
          <p className="text-foreground text-3xl font-semibold tabular-nums leading-none tracking-tight">
            {centerValue ?? total.toLocaleString()}
          </p>
          {centerLabel ? <p className="text-muted-foreground mt-1 text-xs">{centerLabel}</p> : null}
        </div>
      </div>
    </div>
  );
}

/**
 * Compact legend row for DonutChart. Renders one item per segment with
 * a swatch + label + value.
 */
export function DonutLegend({
  segments,
  className,
  showValues = true,
}: {
  segments: DonutSegment[];
  className?: string;
  showValues?: boolean;
}) {
  return (
    <ul className={cn("grid gap-1.5 text-xs", className)}>
      {segments.map((s) => (
        <li key={s.name} className="flex items-center gap-2">
          <span
            aria-hidden="true"
            className="size-2 shrink-0 rounded-full"
            style={{ background: s.color }}
          />
          <span className="text-muted-foreground flex-1 truncate">{s.name}</span>
          {showValues ? (
            <span className="text-foreground font-medium tabular-nums">
              {s.value.toLocaleString()}
            </span>
          ) : null}
        </li>
      ))}
    </ul>
  );
}
